import { useCallback, useMemo } from "react";
import { router, useLocalSearchParams } from "expo-router";
import type {
  ActivityItemViewModel,
  ActivityPageProps,
  ActivityErrorClassification,
  CameraOption,
  SiteOption,
} from "@visioni/a3s-activity/app";
import { SiteId } from "@visioni/a3s-schema";
import { useCameras } from "@src/services/api/hooks";
import { useSites } from "@src/features/cameras/hooks";
import { useTenantId } from "@src/features/tenants";
import { ForbiddenError } from "@src/features/auth";
import { getCameraKeyString, formatCameraLabel } from "@src/utils/key-parser";

export function useActivityPageProps(): ActivityPageProps {
  const tenantId = useTenantId();
  const { data: cameras } = useCameras();
  const { sites, isLoading: sitesLoading } = useSites();

  const cameraOptions = useMemo<CameraOption[]>(
    () =>
      (cameras ?? []).map((c) => ({
        key: getCameraKeyString(c),
        name: c.nickname || formatCameraLabel(getCameraKeyString(c)),
        siteId: c.site.id,
      })),
    [cameras],
  );

  const siteOptions = useMemo<SiteOption[]>(
    () =>
      (sites ?? []).map((s) => ({
        id: SiteId.toString(s.siteId),
        name: s.siteName,
      })),
    [sites],
  );

  const onCameraPress = useCallback(
    (item: ActivityItemViewModel) => {
      if (!item.cameraKey) return;
      const camera = cameras?.find((c) => getCameraKeyString(c) === item.cameraKey);
      if (!camera) return;
      router.push(`/tenants/${tenantId}/sites/${camera.site.id}/cameras/${item.cameraKey}`);
    },
    [cameras, tenantId],
  );

  const classifyError = useCallback((error: Error): ActivityErrorClassification => {
    if (error instanceof ForbiddenError) {
      return { type: "forbidden", message: "You do not have permission to view the activity log." };
    }
    return { type: "error", message: error.message || "Unable to load audit logs. Please try again later." };
  }, []);

  const params = useLocalSearchParams();
  const setParams = useCallback((next: Record<string, string>) => {
    router.setParams(next);
  }, []);

  return {
    cameras: cameraOptions,
    sites: siteOptions,
    sitesLoading,
    onCameraPress,
    formatCameraKey: formatCameraLabel,
    classifyError,
    params,
    setParams,
  };
}
